/* =========================================================================
   IBC 2.0 — ENQUIRY FLOW CONTROLLER
   assets/ibc-enquiry.js — pairs with sections/ibc-enquiry.liquid
   -------------------------------------------------------------------------
   A guided, branching contact experience. It answers the common questions on
   the page and only collects details when a person is genuinely needed.

   This file knows nothing about chocolate. Every customer-facing string
   comes from the section's JSON island: business copy in `nodes`, business
   facts in `config`, interface wording in `ui`, form fields in `fields`.
   Copy changes are a section edit, never a code edit.

   ACTIVATION ORDER — deliberate, and the whole of the progressive
   enhancement story:

     1. find the DOM prerequisites
     2. parse the JSON
     3. validate the content graph
     4. render the guided interface
     5. ONLY THEN disable the plain form's controls, hide it, and turn off
        the browser's native validation on the form element

   If any step fails — missing markup, unparseable JSON, an invalid graph, an
   exception anywhere — the plain Shopify contact form is left exactly as the
   server rendered it: visible, enabled and natively validated. A visitor is
   never shown a broken interface, and never shown nothing at all.

   The form is Shopify's own. This script never posts anything itself: it
   builds `contact[...]` inputs inside that form and lets the browser submit.

   Idempotent: safe to run twice. Tears down on `shopify:section:unload`, so
   the theme editor can add, move and remove the section all day.
   ========================================================================= */

(function () {
  'use strict';

  /* One controller per page load, however many times the asset is included. */
  if (window.IbcEnquiry && window.IbcEnquiry.__loaded) return;

  var NODE_TYPES = ['menu', 'answer', 'form'];
  var FIELD_TYPES = ['text', 'email', 'tel', 'url', 'number', 'date', 'time', 'textarea', 'choice'];
  var ACTION_KINDS = ['call', 'book', 'map', 'review', 'link', 'external', 'mailto', 'node'];

  /* Types where leading and trailing whitespace is not part of the answer. */
  var TRIMMED_TYPES = ['text', 'email', 'tel', 'url', 'textarea'];

  /* Answers that survive a change of branch, so a visitor who backs out of
     one question does not retype their name into the next. */
  var KEEP = ['name', 'email', 'phone'];

  var REQUIRED_UI = [
    'back', 'startAgain', 'send', 'sending', 'required', 'optional',
    'resolveQuestion', 'resolveYes', 'resolveNo',
    'resolvedTitle', 'resolvedBody', 'resolvedAgain',
    'formTitle', 'formLede', 'privacy', 'privacyLink', 'newTab',
    'callPrefix', 'bookPrefix', 'reviewPrefix', 'mapLabel', 'emailUs',
    'chooseOne', 'genericError'
  ];

  /* ----------------------------------------------------------- small utils */

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  /** `deep(obj, 'a.b.c')` without throwing on a missing link in the chain. */
  function deep(obj, dotted) {
    return String(dotted).split('.').reduce(function (o, k) {
      return o == null ? undefined : o[k];
    }, obj);
  }

  /**
   * Whether a dotted path is present in the shape at all, empty or not.
   *
   * This is what separates the two kinds of failure. A path that does not
   * exist is a typo in the content graph and nobody can fix it from the
   * theme editor. A path that exists but holds an empty string is a setting
   * a merchant has not filled in yet, which is ordinary and must not take
   * the contact page down with it.
   */
  function pathExists(obj, dotted) {
    var parts = String(dotted).split('.');
    var cur = obj;
    for (var i = 0; i < parts.length; i++) {
      if (cur == null || typeof cur !== 'object' || !(parts[i] in cur)) return false;
      cur = cur[parts[i]];
    }
    return true;
  }

  /** Today in London, as `yyyy-mm-dd`, for `dateMin` / `dateMax`. A visitor
      in another timezone still gets the shop's idea of "today". */
  function todayInLondon() {
    try {
      return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
    } catch (err) {
      return new Date().toISOString().slice(0, 10);
    }
  }

  function isBlank(value) {
    return value == null || value === '';
  }

  /**
   * Where an action points, and whether it can be rendered at all.
   *
   * Shared by the validator and the renderer so the two cannot drift apart:
   * if this says an action has nowhere to go, the validator warns about it
   * and the renderer leaves it out, for exactly the same reason. `quiet`
   * marks the kinds that are always the secondary choice next to a primary
   * one — calling a shop, opening a map.
   */
  function resolveAction(action, config, nodes) {
    var shop = action.shop ? deep(config, 'shops.' + action.shop) : null;

    if (action.kind === 'node') {
      return { ok: !!(nodes && nodes[action.value]) };
    }
    if (action.kind === 'call') {
      /* Both halves are needed: telHref is where it dials, tel is what the
         button says. One without the other makes a button that lies. */
      return {
        ok: !!(shop && !isBlank(shop.telHref) && !isBlank(shop.tel)),
        href: shop ? 'tel:' + shop.telHref : '',
        quiet: true
      };
    }
    if (action.kind === 'book') {
      return { ok: !!(shop && !isBlank(shop.bookingUrl)), href: shop && shop.bookingUrl, external: true };
    }
    if (action.kind === 'map') {
      return {
        ok: !!(shop && !isBlank(shop.mapUrl)),
        href: shop && shop.mapUrl,
        external: true,
        quiet: true
      };
    }
    if (action.kind === 'review') {
      return { ok: !!(shop && !isBlank(shop.reviewUrl)), href: shop && shop.reviewUrl, external: true };
    }
    if (action.kind === 'mailto') {
      /* The href itself is finished by the renderer, which has the token
         resolver it needs for the subject line. */
      return { ok: !isBlank(config && config.email) };
    }
    if (action.kind === 'link' || action.kind === 'external') {
      var link = deep(config, 'links.' + action.link);
      return {
        ok: !!(link && !isBlank(link.url)),
        href: link && link.url,
        external: !!(link && link.external)
      };
    }
    return { ok: false };
  }

  function actionIsRenderable(action, config, nodes) {
    return resolveAction(action, config, nodes).ok === true;
  }

  /* ------------------------------------------------- content validation ---
     The graph is large, and a typo in it would otherwise stay invisible until
     a customer walked into that branch. So the whole thing is checked once,
     up front, and everything found is listed at once rather than just the
     first problem.

     Findings come back in two piles, and the difference matters:

       fatal  the graph itself is wrong — a node points at an id that does
              not exist, a form asks for a field that was never defined, a
              token names a path that is not in the shape. Nobody can fix
              these from the theme editor and every one of them is a dead end
              waiting for a customer, so the guided interface does not start
              and the plain contact form is left in place.

       warn   a fact is simply missing — a phone number, a map link, a
              policy URL that the merchant has not filled in yet. The flow
              runs; the affected action or token quietly leaves itself out.
              Taking the whole contact page down over an empty setting would
              be far worse than the gap it is complaining about.
  */

  function validateContent(data) {
    var fatal = [];
    var warn = [];
    var result = { fatal: fatal, warn: warn };

    if (!data || typeof data !== 'object') {
      fatal.push('content is not an object');
      return result;
    }

    var nodes = data.nodes;
    var fields = data.fields;
    var config = data.config;
    var ui = data.ui;

    if (!nodes || typeof nodes !== 'object') fatal.push('nodes block missing');
    if (!fields || typeof fields !== 'object') fatal.push('fields block missing');
    if (!config || typeof config !== 'object') fatal.push('config block missing');
    if (!ui || typeof ui !== 'object') fatal.push('ui block missing');
    if (fatal.length) return result;

    REQUIRED_UI.forEach(function (key) {
      if (isBlank(ui[key])) fatal.push('ui."' + key + '" is missing');
    });

    if (!nodes.root) fatal.push('root node is missing');

    /* Derived facts are computed before tokens are checked, so a reference
       such as [[config.shops.soho.phoneFrom]] validates and resolves. */
    Object.keys(config.shops || {}).forEach(function (key) {
      var shop = config.shops[key];
      if (shop && typeof shop.onlineBookingMax === 'number') {
        shop.phoneFrom = shop.onlineBookingMax + 1;
      }
    });

    /* Every [[token]] in every string, wherever it is nested. */
    function checkTokens(value, where) {
      if (typeof value === 'string') {
        (value.match(/\[\[[a-zA-Z0-9_.]+\]\]/g) || []).forEach(function (whole) {
          var path = whole.slice(2, -2);
          if (!pathExists(data, path)) {
            fatal.push(where + ': token ' + whole + ' does not resolve');
          } else if (isBlank(deep(data, path))) {
            warn.push(where + ': token ' + whole + ' resolves to an empty value');
          }
        });
      } else if (Array.isArray(value)) {
        value.forEach(function (v, i) { checkTokens(v, where + '[' + i + ']'); });
      } else if (value && typeof value === 'object') {
        Object.keys(value).forEach(function (k) { checkTokens(value[k], where + '.' + k); });
      }
    }
    checkTokens(nodes, 'nodes');
    checkTokens(fields, 'fields');
    checkTokens(ui, 'ui');

    Object.keys(fields).forEach(function (key) {
      var f = fields[key];
      if (FIELD_TYPES.indexOf(f.type) === -1) {
        fatal.push('field "' + key + '": unsupported type "' + f.type + '"');
      }
      if (!f.contact) fatal.push('field "' + key + '": no contact name');
      if (f.type === 'choice' && !f.options && f.optionsFrom !== 'shops') {
        fatal.push('field "' + key + '": choice field has no options');
      }
    });

    Object.keys(nodes).forEach(function (id) {
      var n = nodes[id];

      if (NODE_TYPES.indexOf(n.type) === -1) {
        fatal.push(id + ': unsupported node type "' + n.type + '"');
      }
      if (id !== 'root' && !n.label) fatal.push(id + ': missing label');

      /* every options/quiet entry points at a node that exists */
      (n.options || []).concat(n.quiet || []).forEach(function (child) {
        if (!nodes[child]) fatal.push(id + ': option references missing node "' + child + '"');
      });
      if (n.type === 'menu' && !(n.options || []).concat(n.quiet || []).length) {
        fatal.push(id + ': menu has no options');
      }

      /* every action can actually be rendered */
      (n.actions || []).forEach(function (a) {
        if (ACTION_KINDS.indexOf(a.kind) === -1) {
          fatal.push(id + ': unsupported action kind "' + a.kind + '"');
          return;
        }
        if (a.kind === 'node' && !nodes[a.value]) {
          fatal.push(id + ': action references missing node "' + a.value + '"');
        }
        if (['call', 'book', 'map', 'review'].indexOf(a.kind) > -1) {
          var shop = a.shop ? deep(config, 'shops.' + a.shop) : null;
          if (!shop) {
            fatal.push(id + ': action references missing shop "' + a.shop + '"');
          } else {
            if (a.kind === 'call' && (isBlank(shop.tel) || isBlank(shop.telHref))) {
              warn.push(id + ': shop "' + a.shop + '" needs tel and telHref');
            }
            if (a.kind === 'book' && isBlank(shop.bookingUrl)) {
              warn.push(id + ': shop "' + a.shop + '" needs bookingUrl');
            }
            if (a.kind === 'map' && (isBlank(shop.mapUrl) || isBlank(shop.address))) {
              warn.push(id + ': shop "' + a.shop + '" needs mapUrl and address');
            }
            if (a.kind === 'review' && isBlank(shop.reviewUrl)) {
              warn.push(id + ': shop "' + a.shop + '" needs reviewUrl');
            }
          }
        }
        if (a.kind === 'link' || a.kind === 'external') {
          if (!pathExists(config, 'links.' + a.link + '.url')) {
            fatal.push(id + ': action references missing link "' + a.link + '"');
          } else if (isBlank(deep(config, 'links.' + a.link + '.url'))) {
            warn.push(id + ': link "' + a.link + '" has no url set');
          }
        }
        if (a.kind === 'mailto' && isBlank(config.email)) {
          warn.push(id + ': mailto action but config.email is missing');
        }
      });

      var f = n.form;
      if (f) {
        if (!f.category) fatal.push(id + ': form has no category');
        if (!f.topic) fatal.push(id + ': form has no topic');
        if (!Array.isArray(f.fields) || !f.fields.length) {
          fatal.push(id + ': form has no fields');
        } else {
          var replyFields = 0;
          var contactNames = {};
          f.fields.forEach(function (key) {
            var field = fields[key];
            if (!field) {
              fatal.push(id + ': form references missing field "' + key + '"');
              return;
            }
            if (field.contact === 'email') replyFields++;
            /* A radio group deliberately repeats one name across its inputs,
               but two different fields must never submit the same name. */
            if (contactNames[field.contact]) {
              fatal.push(id + ': fields "' + contactNames[field.contact] + '" and "' + key +
                '" both submit contact[' + field.contact + ']');
            }
            contactNames[field.contact] = key;
          });
          Object.keys(f.fixed || {}).forEach(function (label) {
            if (contactNames[label]) {
              fatal.push(id + ': fixed value "' + label + '" collides with field "' +
                contactNames[label] + '"');
            }
          });
          if (replyFields !== 1) {
            fatal.push(id + ': needs exactly one contact[email] reply field, found ' + replyFields);
          }
          Object.keys(f.conditional || {}).forEach(function (key) {
            var rule = f.conditional[key];
            if (f.fields.indexOf(key) === -1) {
              fatal.push(id + ': conditional rule for "' + key + '" is not a field of this form');
            }
            if (!rule || f.fields.indexOf(rule.field) === -1) {
              fatal.push(id + ': conditional rule for "' + key + '" depends on a field not in this form');
              return;
            }
            var source = fields[rule.field];
            var options = source && (source.options ||
              (source.optionsFrom === 'shops' ? Object.keys(config.shops).map(function (k) {
                return config.shops[k].name;
              }) : null));
            if (!options || options.indexOf(rule.equals) === -1) {
              fatal.push(id + ': conditional rule for "' + key + '" compares against "' + rule.equals +
                '", which is not an option of "' + rule.field + '"');
            }
          });
        }
      }

      if (n.type !== 'menu') {
        if (!f && !n.terminal) fatal.push(id + ': needs a form or must be terminal');
        if (n.terminal && f) fatal.push(id + ': terminal node must not also have a form');
        if (n.terminal && !(n.actions || []).length) {
          fatal.push(id + ': terminal node has no action at all');
        }
        if (n.terminal && (n.actions || []).length && !(n.actions || []).some(function (a) {
          return actionIsRenderable(a, config, nodes);
        })) {
          warn.push(id + ': terminal node has actions but none can be rendered from the current settings');
        }
        if (f && n.type === 'answer' && !n.resolve && !n.alwaysForm) {
          fatal.push(id + ': answer has a form no customer can reach — set resolve or alwaysForm');
        }
      }
    });

    /* Reachability: a node no route leads to is a branch nobody will ever
       see, which is nearly always a typo in an options list. */
    var seen = {};
    (function walk(id) {
      if (seen[id] || !nodes[id]) return;
      seen[id] = true;
      (nodes[id].options || []).concat(nodes[id].quiet || []).forEach(walk);
      (nodes[id].actions || []).forEach(function (a) {
        if (a.kind === 'node') walk(a.value);
      });
    })('root');
    Object.keys(nodes).forEach(function (id) {
      if (!seen[id]) fatal.push(id + ': unreachable from root');
    });

    Object.keys(fields).forEach(function (key) {
      var used = Object.keys(nodes).some(function (id) {
        return nodes[id].form && (nodes[id].form.fields || []).indexOf(key) > -1;
      });
      if (!used) fatal.push('field "' + key + '" is never used by any route');
    });

    return result;
  }

  /* ================================================================= Flow */

  function Flow(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-ibc-ef-section') || 'x';
    this.headingId = root.getAttribute('data-ibc-ef-heading') || '';
    this.listeners = [];
    this.ready = false;
  }

  /* ----------------------------------------------------------------- setup */

  Flow.prototype.setup = function () {
    var root = this.root;

    this.form = root.querySelector('form');
    var stateEl = root.querySelector('[data-ibc-ef-state]');
    if (!this.form || !stateEl) return false;

    this.serverState = stateEl.getAttribute('data-ibc-ef-state');

    /* A successful post has already replaced the form with the confirmation.
       There is nothing to enhance and nothing to disable. */
    if (this.serverState === 'posted') return true;

    this.island = root.querySelector('[data-ibc-ef-data]');
    this.stepHost = root.querySelector('[data-ibc-ef-step]');
    this.crumbHost = root.querySelector('[data-ibc-ef-crumbs]');
    this.hiddenHost = root.querySelector('[data-ibc-ef-hidden]');
    this.fallback = root.querySelector('[data-ibc-ef-fallback]');
    this.serverError = root.querySelector('[data-ibc-ef-errors]');

    if (!this.island || !this.stepHost || !this.crumbHost || !this.hiddenHost || !this.fallback) {
      return false;
    }

    var data;
    try {
      data = JSON.parse(this.island.textContent);
    } catch (err) {
      console.error('[ibc-enquiry] content JSON did not parse; the plain contact form remains active.', err);
      this.activateFallback();
      return true;
    }

    var report = validateContent(data);
    if (report.warn.length) {
      /* Not fatal: the flow runs and the affected pieces leave themselves
         out. Logged so a merchant's empty setting is findable. */
      console.warn('[ibc-enquiry] content graph has gaps; the guided flow will omit these:\n - ' +
        report.warn.join('\n - '));
    }
    if (report.fatal.length) {
      console.error('[ibc-enquiry] content graph is invalid; the plain contact form remains active:\n - ' +
        report.fatal.join('\n - '));
      this.activateFallback();
      return true;
    }

    this.data = data;
    this.NODES = data.nodes;
    this.FIELDS = data.fields;
    this.CONFIG = data.config;
    this.UI = data.ui;
    this.TODAY = todayInLondon();

    this.answers = {};
    this.path = ['root'];
    this.stage = 'step';
    this.firstPaint = true;

    /* Adjacency, so a deep link cannot assemble a route that no sequence of
       clicks could have produced. */
    this.childMap = {};
    var self = this;
    Object.keys(this.NODES).forEach(function (id) {
      var n = self.NODES[id];
      var kids = (n.options || []).concat(n.quiet || []);
      (n.actions || []).forEach(function (a) { if (a.kind === 'node') kids.push(a.value); });
      self.childMap[id] = kids;
    });

    this.on(this.form, 'submit', function (e) { self.onSubmit(e); });
    this.on(window, 'popstate', function () { self.onPopState(); });
    this.on(window, 'pageshow', function () { self.resetSubmitState(); });

    /* A failed post means the plain form was the thing that failed. Leave it
       exactly as Shopify rendered it, errors, values, native validation and
       all, rather than replacing it with an empty guided step. */
    if (this.serverState === 'error') {
      this.activateFallback();
      return true;
    }

    var fromUrl = this.routeFromLocation();
    if (fromUrl) this.path = fromUrl;

    this.render();
    this.activateGuided();
    return true;
  };

  /** Every listener is recorded so teardown can be complete. */
  Flow.prototype.on = function (target, type, handler) {
    target.addEventListener(type, handler);
    this.listeners.push({ target: target, type: type, handler: handler });
  };

  Flow.prototype.destroy = function () {
    this.listeners.forEach(function (l) {
      l.target.removeEventListener(l.type, l.handler);
    });
    this.listeners = [];
    this.ready = false;
  };

  /* --------------------------------------- switching between the two UIs */

  Flow.prototype.setFallbackEnabled = function (enabled) {
    if (!this.fallback) return;
    var controls = this.fallback.querySelectorAll('input, textarea, select, button');
    Array.prototype.forEach.call(controls, function (control) {
      control.disabled = !enabled;
    });
    this.fallback.setAttribute('aria-hidden', enabled ? 'false' : 'true');
  };

  Flow.prototype.activateGuided = function () {
    /* Order matters and this is the whole of it: the guided interface is
       already in the DOM by the time the plain one is switched off. */
    this.setFallbackEnabled(false);
    this.root.setAttribute('data-ibc-js', 'on');
    this.form.noValidate = true;
    this.ready = true;
  };

  Flow.prototype.activateFallback = function () {
    this.root.removeAttribute('data-ibc-js');
    if (this.form) this.form.noValidate = false;
    this.setFallbackEnabled(true);
    if (this.stepHost) this.stepHost.textContent = '';
    if (this.hiddenHost) this.hiddenHost.textContent = '';
    if (this.crumbHost) {
      this.crumbHost.textContent = '';
      this.crumbHost.hidden = true;
    }
    if (this.form) this.form.removeAttribute('data-ibc-ef-node');
    this.ready = false;
  };

  /* ------------------------------------------------------------ utilities */

  Flow.prototype.node = function (id) {
    return this.NODES[id];
  };

  Flow.prototype.current = function () {
    return this.path[this.path.length - 1];
  };

  Flow.prototype.uid = function (part) {
    return 'ibc-ef-' + this.sectionId + '-' + part;
  };

  /**
   * `[[config.a.b]]` becomes the value at that path.
   *
   * An unresolved token collapses to an empty string. A customer must never
   * be shown `[[config.whatever]]`: a missing fact should read as a slightly
   * thinner sentence, not as machinery showing through. The graph validator
   * has already refused to start the guided interface if any token in the
   * content does not resolve, so this is the belt to that pair of braces.
   */
  Flow.prototype.token = function (value) {
    var data = this.data;
    if (typeof value !== 'string') return value;
    return value.replace(/\[\[([a-zA-Z0-9_.]+)\]\]/g, function (whole, path) {
      var found = deep(data, path);
      return found == null ? '' : String(found);
    });
  };

  /** The human breadcrumb, e.g. "Booking a table > Make a booking > Soho". */
  Flow.prototype.trail = function () {
    var self = this;
    return this.path.slice(1).map(function (id) {
      return self.node(id) && self.node(id).label;
    }).filter(Boolean);
  };

  Flow.prototype.routeIsValid = function (candidate) {
    if (!Array.isArray(candidate) || !candidate.length || candidate[0] !== 'root') return false;
    for (var i = 1; i < candidate.length; i++) {
      if (!this.NODES[candidate[i]]) return false;
      if ((this.childMap[candidate[i - 1]] || []).indexOf(candidate[i]) === -1) return false;
    }
    return true;
  };

  /* ------------------------------------------------------- url and history */

  Flow.prototype.fragmentKey = function () {
    return 'enquiry-' + this.sectionId;
  };

  /* The fragment is an ordered list of `key=value` parts joined by `&`, with
     bare parts (a plain `#contact_form`) left untouched. Only this section's
     own key is ever added, changed or removed, so two enquiry sections on one
     page — and anything else using the fragment — stay out of each other's
     way. */
  Flow.prototype.parseFragment = function (hash) {
    var raw = String(hash || '').replace(/^#/, '');
    if (!raw) return [];
    return raw.split('&').filter(Boolean).map(function (part) {
      var eq = part.indexOf('=');
      if (eq === -1) return { bare: part };
      return { key: part.slice(0, eq), value: part.slice(eq + 1) };
    });
  };

  Flow.prototype.buildFragment = function (parts) {
    var out = parts.map(function (p) {
      return p.bare != null ? p.bare : p.key + '=' + p.value;
    }).filter(Boolean).join('&');
    return out ? '#' + out : '';
  };

  Flow.prototype.urlFor = function (path) {
    var key = this.fragmentKey();
    var value = (path && path.length > 1) ? path.slice(1).join('/') : null;
    var parts = this.parseFragment(window.location.hash).filter(function (p) {
      return p.key !== key;
    });
    if (value != null) parts.push({ key: key, value: value });
    return window.location.pathname + window.location.search + this.buildFragment(parts);
  };

  Flow.prototype.routeFromLocation = function () {
    var key = this.fragmentKey();
    var found = this.parseFragment(window.location.hash).filter(function (p) {
      return p.key === key;
    })[0];
    if (!found) return null;
    var candidate = ['root'].concat(decodeURIComponent(found.value).split('/').filter(Boolean));
    return this.routeIsValid(candidate) ? candidate : ['root'];
  };

  Flow.prototype.writeUrl = function (replace) {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = this.urlFor(this.path);
      if (replace) window.history.replaceState(window.history.state, '', url);
      else window.history.pushState(window.history.state, '', url);
    } catch (err) {
      /* history unavailable — navigation still works, it just is not linkable */
    }
  };

  Flow.prototype.onPopState = function () {
    if (!this.ready || !this.NODES) return;
    var next = this.routeFromLocation() || ['root'];
    this.carryOnly();
    this.path = next;
    this.stage = 'step';
    this.render();
  };

  /* ---------------------------------------------------------- navigation */

  Flow.prototype.carryOnly = function () {
    var answers = this.answers;
    Object.keys(answers).forEach(function (key) {
      if (KEEP.indexOf(key) === -1) delete answers[key];
    });
  };

  Flow.prototype.go = function (id) {
    if (!this.NODES[id]) return;
    this.carryOnly();
    this.path = this.path.concat([id]);
    this.stage = 'step';
    this.writeUrl(false);
    this.render();
  };

  Flow.prototype.back = function () {
    this.carryOnly();
    if (this.stage !== 'step') {
      this.stage = 'step';
    } else if (this.path.length > 1) {
      this.path = this.path.slice(0, -1);
    } else {
      return;
    }
    this.writeUrl(false);
    this.render();
    if (this.path.length === 1) this.focusPageHeading();
  };

  Flow.prototype.restart = function () {
    this.answers = {};
    this.path = ['root'];
    this.stage = 'step';
    /* Replace rather than push, so Back does not revive an abandoned form. */
    this.writeUrl(true);
    this.render();
    this.focusPageHeading();
  };

  Flow.prototype.focusPageHeading = function () {
    var h = this.headingId && document.getElementById(this.headingId);
    if (!h) return;
    if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1');
    h.focus();
  };

  /* ------------------------------------------------------------ rendering */

  Flow.prototype.heading = function (text) {
    var h = el('h3', 'display ibc-ef__q', this.token(text));
    h.id = this.uid('step');
    h.tabIndex = -1;
    this.pendingFocus = h;
    return h;
  };

  Flow.prototype.backButton = function (label, handler) {
    var b = el('button', 'ibc-ef__back', label);
    b.type = 'button';
    b.addEventListener('click', handler);
    return b;
  };

  Flow.prototype.render = function () {
    this.pendingFocus = null;
    this.stepHost.textContent = '';
    this.hiddenHost.textContent = '';
    this.resetSubmitState();
    this.form.removeAttribute('data-ibc-ef-node');
    this.renderCrumbs();

    if (this.stage === 'resolved') this.renderResolved();
    else if (this.stage === 'form') this.renderFormStage();
    else this.renderStep();

    /* Focus moves to the new step heading on every navigation, but not on
       the very first paint — nothing has moved yet, and stealing focus on
       load would drag a visitor past the page's own heading. */
    if (this.firstPaint) {
      this.firstPaint = false;
      return;
    }
    if (this.pendingFocus) this.pendingFocus.focus();
  };

  Flow.prototype.renderCrumbs = function () {
    var self = this;
    var labels = this.trail();
    this.crumbHost.textContent = '';
    if (!labels.length) {
      this.crumbHost.hidden = true;
      return;
    }
    this.crumbHost.hidden = false;
    var list = el('ol', 'ibc-ef__crumb-list');
    labels.forEach(function (label, index) {
      var item = document.createElement('li');
      if (index) {
        var sep = el('span', 'ibc-ef__crumb ibc-ef__crumb--sep', '›');
        sep.setAttribute('aria-hidden', 'true');
        item.appendChild(sep);
      }
      var chip = el('span', 'ibc-ef__crumb', label);
      if (index === labels.length - 1 && self.stage === 'step') {
        chip.setAttribute('aria-current', 'step');
      }
      item.appendChild(chip);
      list.appendChild(item);
    });
    this.crumbHost.appendChild(list);
  };

  Flow.prototype.renderStep = function () {
    var self = this;
    var n = this.node(this.current());
    if (!n) {
      this.path = ['root'];
      n = this.node('root');
    }

    var host = this.stepHost;

    if (this.path.length > 1) {
      host.appendChild(this.backButton('← ' + this.UI.back, function () { self.back(); }));
    }

    if (n.title) host.appendChild(this.heading(n.title));
    if (n.lede) host.appendChild(el('p', 'ibc-ef__lede', this.token(n.lede)));

    if (n.body && n.body.length) {
      var body = el('div', 'ibc-ef__body');
      n.body.forEach(function (paragraph) {
        var text = self.token(paragraph);
        /* A paragraph that was nothing but an unresolved token would render
           as an empty line, so it is left out entirely. */
        if (text && text.trim()) body.appendChild(el('p', null, text));
      });
      if (body.childNodes.length) host.appendChild(body);
    }

    if (n.actions && n.actions.length) this.appendActions(host, n.actions);

    if (n.type === 'menu') {
      var list = el('ul', 'ibc-ef__opts');
      (n.options || []).forEach(function (id) { list.appendChild(self.optionEl(id, false)); });
      (n.quiet || []).forEach(function (id) { list.appendChild(self.optionEl(id, true)); });
      host.appendChild(list);
    }

    var carriesRestart = false;
    if (n.form && (n.alwaysForm || n.type === 'form')) {
      host.appendChild(this.buildForm(n));
    } else if (n.form && n.resolve) {
      host.appendChild(this.resolveEl(n));
      /* The resolve row now carries Start again itself, so the standalone one
         below would be the same control twice on the same screen. */
      carriesRestart = true;
    }

    if (this.path.length > 1 && !carriesRestart) {
      var again = this.backButton(this.UI.startAgain, function () { self.restart(); });
      again.classList.add('ibc-ef__restart');
      host.appendChild(again);
    }
  };

  Flow.prototype.renderFormStage = function () {
    var self = this;
    var n = this.node(this.current());
    if (!n || !n.form) {
      this.stage = 'step';
      return this.renderStep();
    }

    var host = this.stepHost;
    host.appendChild(this.backButton('← ' + this.UI.back, function () { self.back(); }));
    host.appendChild(this.heading(this.UI.formTitle));
    host.appendChild(el('p', 'ibc-ef__lede', this.UI.formLede));
    host.appendChild(this.buildForm(n));

    var again = this.backButton(this.UI.startAgain, function () { self.restart(); });
    again.classList.add('ibc-ef__restart');
    host.appendChild(again);
  };

  Flow.prototype.renderResolved = function () {
    var self = this;
    var box = el('div', 'ibc-ef__done');
    var tick = el('div', 'ibc-ef__tick', '✓');
    tick.setAttribute('aria-hidden', 'true');
    box.appendChild(tick);
    box.appendChild(this.heading(this.UI.resolvedTitle));
    box.appendChild(el('p', 'ibc-ef__lede', this.UI.resolvedBody));

    var again = el('button', 'btn btn--ghost', this.UI.resolvedAgain);
    again.type = 'button';
    again.addEventListener('click', function () { self.restart(); });
    box.appendChild(again);

    this.stepHost.appendChild(box);
  };

  /* --------------------------------------------------------------- options */

  Flow.prototype.optionEl = function (id, quiet) {
    var self = this;
    var target = this.node(id);
    var item = document.createElement('li');
    var button = el('button', 'ibc-ef__opt' + (quiet ? ' ibc-ef__opt--quiet' : ''));
    button.type = 'button';
    button.appendChild(el('span', null, target.label));
    var chevron = el('span', 'ibc-ef__opt-chev', '›');
    chevron.setAttribute('aria-hidden', 'true');
    button.appendChild(chevron);
    button.addEventListener('click', function () { self.go(id); });
    item.appendChild(button);
    return item;
  };

  /* --------------------------------------------------------------- actions */

  /**
   * Consecutive actions carrying the same `group` are boxed together under
   * that label — two map links belonging to two different shops, say. A run
   * ends as soon as an action has a different group or none.
   */
  Flow.prototype.appendActions = function (host, actions) {
    var self = this;
    var container = el('div', 'ibc-ef__actions');
    var currentGroup = null;
    var groupBox = null;

    actions.forEach(function (action) {
      var group = action.group ? self.token(action.group) : null;
      if (group !== currentGroup) {
        currentGroup = group;
        if (group) {
          groupBox = el('div', 'ibc-ef__action-group');
          groupBox.appendChild(el('p', 'ibc-ef__action-group-title', group));
          container.appendChild(groupBox);
        } else {
          groupBox = null;
        }
      }
      var control = self.actionEl(action);
      if (control) (groupBox || container).appendChild(control);
    });

    /* A group whose every action dropped out would otherwise leave a titled
       box with nothing in it. */
    Array.prototype.forEach.call(container.querySelectorAll('.ibc-ef__action-group'), function (box) {
      if (!box.querySelector('a, button')) box.remove();
    });

    if (container.childNodes.length) host.appendChild(container);
  };

  Flow.prototype.actionEl = function (action) {
    var self = this;
    var label = this.token(action.label || '');
    var ghost = action.ghost === true;

    /* `node` is the one action that navigates rather than leaves. */
    if (action.kind === 'node') {
      var button = el('button', 'btn btn--ghost ibc-ef__action', label);
      button.type = 'button';
      button.addEventListener('click', function () { self.go(action.value); });
      return button;
    }

    /* An action whose underlying setting is empty leaves itself out rather
       than rendering a button that goes nowhere. */
    var target = resolveAction(action, this.CONFIG, this.NODES);
    if (!target.ok) return null;

    var shop = action.shop ? this.CONFIG.shops[action.shop] : null;
    var external = target.external === true;
    var href = target.href;
    var quiet = ghost || target.quiet === true;

    if (action.kind === 'call') {
      label = label || (this.UI.callPrefix + ' ' + shop.name + ', ' + shop.tel);
    } else if (action.kind === 'book') {
      label = label || (this.UI.bookPrefix + ' ' + shop.name);
    } else if (action.kind === 'map') {
      /* The address makes the better label, but the shop name still works
         if the address setting has not been filled in. */
      label = label || (this.UI.mapLabel + ' — ' + (shop.address || shop.name));
    } else if (action.kind === 'review') {
      label = label || (this.UI.reviewPrefix + ' ' + shop.name);
    } else if (action.kind === 'mailto') {
      label = label || this.UI.emailUs;
      href = 'mailto:' + this.CONFIG.email +
        '?subject=' + encodeURIComponent(this.token(action.subject || ''));
    }

    if (!href || !label) return null;

    var anchor = el('a', 'btn' + (quiet ? ' btn--ghost' : '') + ' ibc-ef__action');
    anchor.href = href;
    anchor.appendChild(document.createTextNode(label));

    if (external) {
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      /* Said out loud, not just implied by an icon. */
      anchor.appendChild(el('span', 'ibc-ef__sr', ' (' + this.UI.newTab + ')'));
    }

    return anchor;
  };

  /* ---------------------------------------------------- "did that answer?" */

  /* TWO QUIET CONTROLS, SIDE BY SIDE — Start again, then I still need help.

     This row used to ask "Did that answer your question?" above a pair of
     full-width pills, Yes and No. It is now the two small text controls
     below, at the size Start again has always been: `.ibc-ef__back` is the
     quiet treatment, not `.btn`, so these match the rest of the flow's
     navigation rather than competing with the answer above them.

     WHAT WENT WITH THE YES BUTTON. "Yes, thank you" was the only route into
     the `resolved` stage — the tick, "Glad we could help", and "Ask about
     something else". Start again does what that panel's own button did, one
     click earlier, so the flow still ends somewhere sensible; but the panel
     itself is now unreachable. `renderResolved` and the resolvedTitle /
     resolvedBody / resolvedAgain strings are left in place, unused, so
     putting it back is a small change rather than a rebuild. */
  Flow.prototype.resolveEl = function (n) {
    var self = this;
    var box = el('div', 'ibc-ef__resolve');
    var row = el('div', 'ibc-ef__resolve-row');

    var again = el('button', 'ibc-ef__back ibc-ef__resolve-btn', this.UI.startAgain);
    again.type = 'button';
    again.addEventListener('click', function () {
      self.restart();
    });

    var help = el('button', 'ibc-ef__back ibc-ef__resolve-btn', this.token(n.resolveNo || this.UI.resolveNo));
    help.type = 'button';
    help.addEventListener('click', function () {
      self.stage = 'form';
      self.render();
    });

    row.appendChild(again);
    row.appendChild(help);
    box.appendChild(row);
    return box;
  };

  /* ----------------------------------------------------------------- forms */

  Flow.prototype.fieldIsActive = function (n, key) {
    var rule = (n.form.conditional || {})[key];
    if (!rule) return true;
    return this.answers[rule.field] === rule.equals;
  };

  /**
   * A conditional field that does not apply is hidden AND disabled. Disabled
   * is the part that matters: a disabled control is not submitted and is not
   * validated by the browser, so an irrelevant question can neither block the
   * send nor turn up as a blank line in the notification email.
   */
  Flow.prototype.applyConditionals = function (n, container) {
    var self = this;
    Object.keys(n.form.conditional || {}).forEach(function (key) {
      var wrapper = container.querySelector('[data-field="' + key + '"]');
      if (!wrapper) return;
      var active = self.fieldIsActive(n, key);
      wrapper.hidden = !active;
      if (wrapper.tagName === 'FIELDSET') wrapper.disabled = !active;
      Array.prototype.forEach.call(wrapper.querySelectorAll('input, textarea, select'), function (input) {
        input.disabled = !active;
      });
      if (!active) self.clearFieldError(wrapper);
    });
  };

  Flow.prototype.requiredMarker = function () {
    var holder = document.createDocumentFragment();
    var mark = el('span', 'ibc-ef__req', ' *');
    mark.setAttribute('aria-hidden', 'true');
    holder.appendChild(mark);
    holder.appendChild(el('span', 'ibc-ef__sr', ' ' + this.UI.required));
    return holder;
  };

  Flow.prototype.optionalMarker = function () {
    return el('span', 'ibc-ef__opt-flag', ' (' + this.UI.optional + ')');
  };

  Flow.prototype.buildInput = function (key, field, inputId, errorId) {
    var self = this;
    var input;

    if (field.type === 'textarea') {
      input = document.createElement('textarea');
      input.rows = 6;
    } else {
      input = document.createElement('input');
      input.type = field.type;
      if (field.min != null) input.min = field.min;
      if (field.max != null) input.max = field.max;
      /* "today" is resolved in the shop's timezone, not the visitor's. */
      if (field.dateMin === 'today') input.min = this.TODAY;
      if (field.dateMax === 'today') input.max = this.TODAY;
    }

    input.className = 'ibc-ef__input';
    input.id = inputId;
    input.name = 'contact[' + field.contact + ']';

    if (field.maxlength) input.maxLength = field.maxlength;
    if (field.placeholder) input.placeholder = this.token(field.placeholder);
    if (field.autocomplete) input.autocomplete = field.autocomplete;
    if (field.inputmode) input.inputMode = field.inputmode;
    if (field.autocapitalize) input.setAttribute('autocapitalize', field.autocapitalize);
    if (field.spellcheck === false) input.spellcheck = false;
    if (field.required) {
      input.required = true;
      input.setAttribute('aria-required', 'true');
    }
    input.setAttribute('aria-describedby', errorId);
    if (this.answers[key] != null) input.value = this.answers[key];

    input.addEventListener('input', function () { self.answers[key] = input.value; });
    input.addEventListener('change', function () { self.answers[key] = input.value; });
    return input;
  };

  /**
   * A choice is a real fieldset with a real legend and real radios — tappable
   * options rather than a select, which on a phone is a far better target and
   * shows every option at once.
   */
  Flow.prototype.buildChoice = function (n, key, field, container) {
    var self = this;
    var wrapper = document.createElement('fieldset');
    wrapper.className = 'ibc-ef__field ibc-ef__field--choice ibc-ef__field--wide';
    wrapper.setAttribute('data-field', key);

    var errorId = this.uid('err-' + key);

    var legend = el('legend', 'ibc-ef__label');
    legend.appendChild(document.createTextNode(this.token(field.label)));
    if (field.required) legend.appendChild(this.requiredMarker());
    else legend.appendChild(this.optionalMarker());
    wrapper.appendChild(legend);

    var options = field.optionsFrom === 'shops'
      ? Object.keys(this.CONFIG.shops).map(function (k) { return self.CONFIG.shops[k].name; })
      : (field.options || []);

    var row = el('div', 'ibc-ef__choice');
    options.forEach(function (value, index) {
      var item = el('label', 'ibc-ef__choice-item');
      var input = document.createElement('input');
      input.type = 'radio';
      input.className = 'ibc-ef__choice-input';
      input.name = 'contact[' + field.contact + ']';
      input.value = value;
      input.id = self.uid('field-' + key + '-' + index);
      /* The message belongs to the group, but every radio points at it so it
         is announced whichever one has focus. */
      input.setAttribute('aria-describedby', errorId);
      if (field.required) {
        input.required = true;
        input.setAttribute('aria-required', 'true');
      }
      if (self.answers[key] === value) input.checked = true;
      input.addEventListener('change', function () {
        self.answers[key] = value;
        self.clearFieldError(wrapper);
        self.applyConditionals(n, container);
      });
      item.appendChild(input);
      item.appendChild(el('span', 'ibc-ef__choice-face', value));
      row.appendChild(item);
    });
    wrapper.appendChild(row);

    var error = el('small', 'ibc-ef__err');
    error.id = errorId;
    wrapper.appendChild(error);
    return wrapper;
  };

  Flow.prototype.clearFieldError = function (wrapper) {
    wrapper.removeAttribute('aria-invalid');
    wrapper.classList.remove('is-invalid');
    var error = wrapper.querySelector('.ibc-ef__err');
    if (error) error.textContent = '';
    Array.prototype.forEach.call(wrapper.querySelectorAll('input, textarea, select'), function (input) {
      input.removeAttribute('aria-invalid');
    });
  };

  Flow.prototype.setFieldError = function (wrapper, message) {
    wrapper.setAttribute('aria-invalid', 'true');
    wrapper.classList.add('is-invalid');
    var error = wrapper.querySelector('.ibc-ef__err');
    if (error) error.textContent = message;
    /* Every control in the group carries the invalid state, so it is
       announced whichever one receives focus. */
    Array.prototype.forEach.call(wrapper.querySelectorAll('input, textarea, select'), function (input) {
      input.setAttribute('aria-invalid', 'true');
    });
  };

  Flow.prototype.buildForm = function (n) {
    var self = this;
    var container = document.createElement('div');
    var grid = el('div', 'ibc-ef__fields');

    n.form.fields.forEach(function (key) {
      var field = self.FIELDS[key];
      if (!field) return;

      if (field.type === 'choice') {
        grid.appendChild(self.buildChoice(n, key, field, container));
        return;
      }

      var inputId = self.uid('field-' + key);
      var errorId = self.uid('err-' + key);
      var wrapper = el('div', 'ibc-ef__field' +
        (field.type === 'textarea' ? ' ibc-ef__field--wide' : ''));
      wrapper.setAttribute('data-field', key);

      var label = el('label', 'ibc-ef__label');
      label.setAttribute('for', inputId);
      label.appendChild(document.createTextNode(self.token(field.label)));
      if (field.required) label.appendChild(self.requiredMarker());
      else label.appendChild(self.optionalMarker());
      wrapper.appendChild(label);

      var input = self.buildInput(key, field, inputId, errorId);
      input.addEventListener('input', function () { self.clearFieldError(wrapper); });
      wrapper.appendChild(input);

      var error = el('small', 'ibc-ef__err');
      error.id = errorId;
      wrapper.appendChild(error);

      grid.appendChild(wrapper);
    });

    container.appendChild(grid);

    var privacy = el('p', 'ibc-ef__privacy', this.UI.privacy + ' ');
    var privacyUrl = deep(this.CONFIG, 'links.privacy.url');
    if (privacyUrl) {
      var privacyLink = el('a', null, this.UI.privacyLink);
      privacyLink.href = privacyUrl;
      privacy.appendChild(privacyLink);
      privacy.appendChild(document.createTextNode('.'));
    }
    container.appendChild(privacy);

    var submit = el('button', 'btn ibc-ef__submit', this.token(n.form.cta || this.UI.send));
    submit.type = 'submit';
    submit.setAttribute('data-ibc-ef-submit', '');
    submit.setAttribute('data-ibc-ef-label', submit.textContent);
    container.appendChild(submit);

    this.writeHidden(n);
    this.applyConditionals(n, container);
    this.form.setAttribute('data-ibc-ef-node', this.current());
    return container;
  };

  /**
   * The categorisation travels as four clearly named custom fields, because
   * Shopify fixes the real notification Subject header and a theme cannot
   * change it. These appear as labelled lines in the body of the email, which
   * is what staff filter on.
   */
  Flow.prototype.writeHidden = function (n) {
    var host = this.hiddenHost;
    host.textContent = '';

    var add = function (label, value) {
      if (isBlank(value)) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'contact[' + label + ']';
      input.value = value;
      host.appendChild(input);
    };

    add('Enquiry category', n.form.category);
    add('Enquiry topic', n.form.topic);
    add('Enquiry path', this.trail().join(' > '));
    add('Enquiry reference', this.current());

    var fixed = n.form.fixed || {};
    Object.keys(fixed).forEach(function (key) { add(key, fixed[key]); });
  };

  /* ------------------------------------------------------------ validation */

  Flow.prototype.messageFor = function (field, input) {
    var v = input.validity;
    if (v.valueMissing) return field.error || this.UI.genericError;
    if (v.typeMismatch) return field.typeError || field.error || this.UI.genericError;
    if (v.rangeUnderflow || v.rangeOverflow || v.stepMismatch) {
      return field.rangeError || field.error || this.UI.genericError;
    }
    if (v.tooLong) return field.error || this.UI.genericError;
    return field.error || this.UI.genericError;
  };

  Flow.prototype.validate = function (n) {
    var self = this;
    var firstInvalid = null;
    var invalidKeys = [];

    n.form.fields.forEach(function (key) {
      var field = self.FIELDS[key];
      var wrapper = self.stepHost.querySelector('[data-field="' + key + '"]');
      /* A hidden conditional field is not asked and so is not checked. */
      if (!field || !wrapper || wrapper.hidden) return;

      self.clearFieldError(wrapper);

      if (field.type === 'choice') {
        var checked = wrapper.querySelector('input[type="radio"]:checked');
        self.answers[key] = checked ? checked.value : '';
        if (field.required && !checked) {
          self.setFieldError(wrapper, field.error || self.UI.chooseOne);
          invalidKeys.push(key);
          if (!firstInvalid) firstInvalid = wrapper.querySelector('input[type="radio"]');
        }
        return;
      }

      var input = wrapper.querySelector('input, textarea, select');
      if (!input || input.disabled) return;

      /* Whitespace only is the same as empty; spacing inside is kept. */
      if (TRIMMED_TYPES.indexOf(field.type) > -1) {
        var trimmed = input.value.trim();
        if (trimmed !== input.value) input.value = trimmed;
      }
      self.answers[key] = input.value;

      if (!input.checkValidity()) {
        self.setFieldError(wrapper, self.messageFor(field, input));
        invalidKeys.push(key);
        if (!firstInvalid) firstInvalid = input;
      }
    });

    return { firstInvalid: firstInvalid, invalidKeys: invalidKeys };
  };

  /* --------------------------------------------------------------- submit */

  Flow.prototype.setBusy = function () {
    this.submitting = true;
    this.form.setAttribute('aria-busy', 'true');
    var submit = this.stepHost.querySelector('[data-ibc-ef-submit]');
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-disabled', 'true');
      submit.textContent = this.UI.sending;
    }
  };

  Flow.prototype.resetSubmitState = function () {
    this.submitting = false;
    if (this.form) this.form.removeAttribute('aria-busy');
    if (!this.stepHost) return;
    var submit = this.stepHost.querySelector('[data-ibc-ef-submit]');
    if (submit) {
      submit.disabled = false;
      submit.removeAttribute('aria-disabled');
      var label = submit.getAttribute('data-ibc-ef-label');
      if (label) submit.textContent = label;
    }
  };

  /**
   * Guards against the plain form and the guided one both contributing a
   * value. It should be impossible — the plain controls are disabled the
   * moment the guided interface renders — but a duplicate `contact[email]`
   * would silently corrupt an enquiry, so it is worth one check.
   */
  Flow.prototype.hasNoDuplicates = function () {
    var counts = {};
    var data;
    try {
      data = new FormData(this.form);
    } catch (err) {
      return true;
    }
    data.forEach(function (value, name) {
      counts[name] = (counts[name] || 0) + 1;
    });
    var single = ['contact[email]', 'contact[Your name]', 'contact[Message]'];
    var duplicates = single.filter(function (name) { return counts[name] > 1; });
    if (duplicates.length) {
      console.warn('[ibc-enquiry] duplicate submitted fields: ' + duplicates.join(', ') +
        '. Re-disabling the plain form and re-checking.');
      this.setFallbackEnabled(false);
      return false;
    }
    return true;
  };

  Flow.prototype.onSubmit = function (event) {
    var nodeId = this.form.getAttribute('data-ibc-ef-node');
    var n = nodeId && this.NODES ? this.NODES[nodeId] : null;
    var guidedFields = this.stepHost && this.stepHost.querySelector('[data-field]');

    /* Not our submission: the plain form keeps native validation and native
       behaviour, and this handler stays out of its way entirely. */
    if (!this.ready || !n || !n.form || !guidedFields) return;

    if (this.submitting) {
      event.preventDefault();
      return;
    }

    var result = this.validate(n);
    if (result.firstInvalid) {
      event.preventDefault();
      result.firstInvalid.focus();
      return;
    }

    if (!this.hasNoDuplicates() && !this.hasNoDuplicates()) {
      event.preventDefault();
      return;
    }

    this.setBusy();
  };

  /* ============================================================ activation */

  var instances = [];

  function findInstance(root) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].root === root) return i;
    }
    return -1;
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-ibc-enquiry]');
    Array.prototype.forEach.call(roots, function (root) {
      if (findInstance(root) > -1) return;

      var flow = new Flow(root);
      instances.push(flow);

      try {
        if (!flow.setup()) {
          /* Prerequisites are not there. The plain form is what the server
             sent and it is still enabled, so there is nothing to undo. */
          console.error('[ibc-enquiry] required markup was not found; the plain contact form remains active.');
        }
      } catch (err) {
        console.error('[ibc-enquiry] initialisation failed; the plain contact form remains active.', err);
        try {
          flow.activateFallback();
        } catch (inner) {
          /* Nothing further is safe to do; the server markup already works. */
        }
      }
    });
  }

  function unload(event) {
    var target = event && event.target;
    for (var i = instances.length - 1; i >= 0; i--) {
      var flow = instances[i];
      if (!target || target === flow.root || target.contains(flow.root)) {
        flow.destroy();
        instances.splice(i, 1);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* The theme editor rebuilds a section's markup in place, so the old
     instance is thrown away and a new one is built against the new DOM. */
  document.addEventListener('shopify:section:load', function (event) {
    unload(event);
    init(event.target || document);
  });
  document.addEventListener('shopify:section:unload', unload);

  window.IbcEnquiry = {
    __loaded: true,
    init: init,
    validate: validateContent
  };
})();
